/**
 * 验证会话展示面板
 *
 * 展示航班费用验证的结果，包括：
 * - 汇总状态
 * - 每个航班的验证结果
 * - 调试模式：查看原始验证数据
 */

import React, { useState } from "react";
import { Card, Tag, Space, Typography, Progress, List, Badge, Collapse, Button, Modal, Tooltip, Divider, Alert, Empty } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, QuestionCircleOutlined, BugOutlined, EyeOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;
const { Panel } = Collapse;

/**
 * 验证状态标签
 */
const ValidationStatusTag = ({ status }) => {
    const statusConfig = {
        PASSED: { color: "success", icon: <CheckCircleOutlined />, text: "验证通过" },
        HAS_ERRORS: { color: "error", icon: <CloseCircleOutlined />, text: "发现问题" },
        HAS_ERROR: { color: "error", icon: <CloseCircleOutlined />, text: "有问题" },
        PARTIAL: { color: "warning", icon: <ExclamationCircleOutlined />, text: "部分完成" },
        SCHEDULING: { color: "processing", icon: <QuestionCircleOutlined />, text: "调度中" },
        IN_PROGRESS: { color: "processing", icon: <QuestionCircleOutlined />, text: "验证中" },
        SKIPPED: { color: "default", icon: <QuestionCircleOutlined />, text: "已跳过" },
    };

    const config = statusConfig[status] || { color: "default", icon: null, text: status };

    return (
        <Tag color={config.color} icon={config.icon}>
            {config.text}
        </Tag>
    );
};

/**
 * 航班验证结果卡片
 */
const FlightResultCard = ({ flightKey, result, onViewDetails }) => {
    const [flight_number, flight_date] = flightKey.split("|");

    return (
        <Card
            size="small"
            style={{
                marginBottom: 8,
                borderLeft: result.status === "HAS_ERROR" ? "3px solid #ff4d4f" : "3px solid #52c41a",
            }}
        >
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Space style={{ justifyContent: "space-between", width: "100%" }}>
                    <Space>
                        <Text strong>{flight_number}</Text>
                        <Text type="secondary">{flight_date}</Text>
                    </Space>
                    <ValidationStatusTag status={result.status} />
                </Space>

                <Space>
                    <Text type="secondary">金额: ¥{result.total?.toLocaleString() || 0}</Text>
                    {result.vote_count > 1 && (
                        <Tooltip title={`${result.vote_count} 次验证, ${result.error_count} 次发现问题`}>
                            <Badge count={result.vote_count} style={{ backgroundColor: result.amounts_consistent ? "#52c41a" : "#faad14" }} />
                        </Tooltip>
                    )}
                </Space>

                {result.discrepancies && result.discrepancies.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                        <Text type="danger" style={{ fontSize: 12 }}>
                            差异: {result.discrepancies.map((d) => `${d.field || "未知字段"}`).join(", ")}
                        </Text>
                    </div>
                )}

                {onViewDetails && (
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onViewDetails(flightKey, result)} style={{ padding: 0 }}>
                        查看详情
                    </Button>
                )}
            </Space>
        </Card>
    );
};

/**
 * 验证会话面板主组件
 */
const ValidationSessionPanel = ({ validationSession, structuredData }) => {
    const [debugModalVisible, setDebugModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedFlight, setSelectedFlight] = useState(null);

    // 如果没有验证会话，显示空状态
    if (!validationSession) {
        return (
            <Card size="small" title="航班费用验证">
                <Empty description="暂无验证数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
        );
    }

    const { status, session_id, created_at, completed_at, flight_count, task_count, failed_task_count, aggregated_result, tasks } = validationSession;

    // 计算进度
    const isCompleted = status === "COMPLETED";
    const summary = aggregated_result?.summary || {};

    const handleViewFlightDetails = (flightKey, result) => {
        setSelectedFlight({ flightKey, result });
        setDetailModalVisible(true);
    };

    return (
        <>
            <Card
                size="small"
                title={
                    <Space>
                        <span>航班费用验证</span>
                        <ValidationStatusTag status={isCompleted ? aggregated_result?.status : status} />
                    </Space>
                }
                extra={
                    <Button type="text" icon={<BugOutlined />} onClick={() => setDebugModalVisible(true)} title="查看原始数据">
                        调试
                    </Button>
                }
            >
                {/* 汇总信息 */}
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    {/* 会话状态 */}
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            会话ID: {session_id}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            创建于: {created_at} {completed_at && `| 完成于: ${completed_at}`}
                        </Text>
                    </div>

                    {/* 进度条 */}
                    {!isCompleted && (
                        <div>
                            <Text>验证进度</Text>
                            <Progress percent={Math.round((tasks?.filter((t) => t.status !== "PENDING").length / task_count) * 100)} status="active" format={() => `${tasks?.filter((t) => t.status !== "PENDING").length || 0}/${task_count}`} />
                        </div>
                    )}

                    {/* 汇总结果 */}
                    {isCompleted && aggregated_result && (
                        <>
                            <Divider style={{ margin: "12px 0" }} />

                            <Space size={24}>
                                <div style={{ textAlign: "center" }}>
                                    <Title level={3} style={{ margin: 0, color: "#52c41a" }}>
                                        {summary.passed || 0}
                                    </Title>
                                    <Text type="secondary">通过</Text>
                                </div>
                                <div style={{ textAlign: "center" }}>
                                    <Title level={3} style={{ margin: 0, color: "#ff4d4f" }}>
                                        {summary.errors || 0}
                                    </Title>
                                    <Text type="secondary">问题</Text>
                                </div>
                                <div style={{ textAlign: "center" }}>
                                    <Title level={3} style={{ margin: 0, color: "#d9d9d9" }}>
                                        {summary.skipped || 0}
                                    </Title>
                                    <Text type="secondary">跳过</Text>
                                </div>
                                {summary.failed_tasks > 0 && (
                                    <div style={{ textAlign: "center" }}>
                                        <Title level={3} style={{ margin: 0, color: "#faad14" }}>
                                            {summary.failed_tasks}
                                        </Title>
                                        <Text type="secondary">任务失败</Text>
                                    </div>
                                )}
                            </Space>

                            {/* 问题航班列表 */}
                            {summary.errors > 0 && (
                                <>
                                    <Divider style={{ margin: "12px 0" }} />
                                    <Alert message="以下航班验证发现问题，请人工复核" type="warning" showIcon style={{ marginBottom: 8 }} />

                                    {Object.entries(aggregated_result.flight_results || {})
                                        .filter(([, r]) => r.status === "HAS_ERROR")
                                        .map(([flightKey, result]) => (
                                            <FlightResultCard key={flightKey} flightKey={flightKey} result={result} onViewDetails={handleViewFlightDetails} />
                                        ))}
                                </>
                            )}

                            {/* 通过的航班（折叠显示） */}
                            {summary.passed > 0 && (
                                <Collapse ghost>
                                    <Panel header={`查看通过的航班 (${summary.passed}个)`} key="passed">
                                        {Object.entries(aggregated_result.flight_results || {})
                                            .filter(([, r]) => r.status === "PASSED")
                                            .map(([flightKey, result]) => (
                                                <FlightResultCard key={flightKey} flightKey={flightKey} result={result} onViewDetails={handleViewFlightDetails} />
                                            ))}
                                    </Panel>
                                </Collapse>
                            )}
                        </>
                    )}
                </Space>
            </Card>

            {/* 调试模态框 - 查看原始数据 */}
            <Modal title="验证原始数据 (调试)" open={debugModalVisible} onCancel={() => setDebugModalVisible(false)} footer={null} width={800}>
                <Collapse defaultActiveKey={["session"]}>
                    <Panel header="验证会话" key="session">
                        <pre
                            style={{
                                background: "#f5f5f5",
                                padding: 12,
                                borderRadius: 4,
                                fontSize: 11,
                                maxHeight: 300,
                                overflow: "auto",
                            }}
                        >
                            {JSON.stringify(validationSession, null, 2)}
                        </pre>
                    </Panel>
                    <Panel header="完整 structured_data" key="structured">
                        <pre
                            style={{
                                background: "#f5f5f5",
                                padding: 12,
                                borderRadius: 4,
                                fontSize: 11,
                                maxHeight: 400,
                                overflow: "auto",
                            }}
                        >
                            {JSON.stringify(structuredData, null, 2)}
                        </pre>
                    </Panel>
                </Collapse>
            </Modal>

            {/* 航班详情模态框 */}
            <Modal title={selectedFlight ? `航班验证详情: ${selectedFlight.flightKey}` : "航班详情"} open={detailModalVisible} onCancel={() => setDetailModalVisible(false)} footer={null} width={700}>
                {selectedFlight && (
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                        <div>
                            <Text strong>状态: </Text>
                            <ValidationStatusTag status={selectedFlight.result.status} />
                        </div>
                        <div>
                            <Text strong>验证总金额: </Text>
                            <Text>¥{selectedFlight.result.total?.toLocaleString() || 0}</Text>
                        </div>
                        <div>
                            <Text strong>验证次数: </Text>
                            <Text>{selectedFlight.result.vote_count || 1}</Text>
                            {selectedFlight.result.error_count > 0 && <Text type="danger"> ({selectedFlight.result.error_count} 次发现问题)</Text>}
                        </div>
                        <div>
                            <Text strong>金额一致: </Text>
                            <Text>{selectedFlight.result.amounts_consistent ? "是" : "否"}</Text>
                        </div>

                        {selectedFlight.result.discrepancies && selectedFlight.result.discrepancies.length > 0 && (
                            <>
                                <Divider style={{ margin: "12px 0" }} />
                                <Text strong>发现的差异:</Text>
                                <List
                                    size="small"
                                    bordered
                                    dataSource={selectedFlight.result.discrepancies}
                                    renderItem={(d, idx) => (
                                        <List.Item key={idx}>
                                            <Space direction="vertical" size={0}>
                                                <Text strong>{d.field || "字段"}</Text>
                                                <Text type="secondary">
                                                    期望: {d.expected} | 实际: {d.actual}
                                                </Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </>
                        )}

                        <Divider style={{ margin: "12px 0" }} />
                        <Text strong>原始验证数据:</Text>
                        <pre
                            style={{
                                background: "#f5f5f5",
                                padding: 12,
                                borderRadius: 4,
                                fontSize: 11,
                                maxHeight: 300,
                                overflow: "auto",
                            }}
                        >
                            {JSON.stringify(selectedFlight.result.votes || selectedFlight.result, null, 2)}
                        </pre>
                    </Space>
                )}
            </Modal>
        </>
    );
};

export default ValidationSessionPanel;
